/** All env-derived URLs live behind one accessor. Saves grepping the
 * codebase for `import.meta.env` and lets us swap the Tauri config in.
 *
 * No `marketingApiUrl` / `salesApiUrl` here on purpose — the browser
 * never speaks to those services. Marketing and Sales are reached
 * through the dashboard server's proxy (`/api/marketing/*`,
 * `/api/sales/*`). That keeps every agent box VPC-private in
 * production. If you need to reach an agent service directly during
 * dev, do it via curl from your machine, not from the web app. */
export const env = Object.freeze({
  /**
   * API base. Default is "" = SAME-ORIGIN relative (`api.ts` builds
   * `${apiUrl}/api/...`, so "" ⇒ `/api/...`). This is correct for any
   * reverse-proxied deployment: behind Caddy on the staff-edge box
   * `/api/*` already routes to :4000 (Fastify) and `/api/v1/*` to
   * :5174→crm-api, on whatever hostname the dashboard is served from.
   *
   * A hardcoded `http://localhost:4000` default was COMPILED INTO the
   * production bundle and made the browser fetch the *user's own*
   * machine → "Failed to fetch" on login. Localhost is now opt-IN for
   * local dev only (docker-compose.yml sets it explicitly); production
   * (docker-compose.prod.yml) sets it empty. Pinned by env.test.ts.
   */
  apiUrl: import.meta.env.VITE_DASHBOARD_API_URL ?? "",
  /**
   * Public, externally-reachable origin of the STAFF dashboard itself
   * (`https://crm.xopnetworks.com` in prod). Used to build *shareable*
   * deep-links to in-app staff records (a CRM prospect/customer, a sales
   * lead) for "Copy link" / "Share via email" — those links LEAVE the app,
   * so they must never be `window.location.origin` (which is `tauri://`
   * on desktop, `localhost` in dev — neither openable by a recipient).
   * Empty by default: `dashboardPublicOrigin()` (components/share/
   * appShareLinks.ts) then prefers the live `window.location.origin` when
   * it is already a public http(s) host (the deployed web app IS
   * crm.xopnetworks.com) and otherwise falls back to the known prod host.
   */
  dashboardWebUrl: import.meta.env.VITE_DASHBOARD_WEB_URL ?? "",
  supportWebUrl:
    import.meta.env.VITE_SUPPORT_CENTER_WEB_URL ?? "http://localhost:5173",
  crmWebUrl: import.meta.env.VITE_CRM_WEB_URL ?? "http://localhost:3001",
  docsUrl:
    import.meta.env.VITE_DOCS_URL ??
    `${import.meta.env.VITE_SUPPORT_CENTER_WEB_URL ?? "http://localhost:5173"}/portal/knowledge`,
  /** VAPID public key for browser Web Push. Empty ⇒ the browser-push
   * channel is unavailable (the prefs toggle shows a "not configured"
   * hint). Must match the server's VAPID_PUBLIC_KEY. */
  vapidPublicKey: import.meta.env.VITE_VAPID_PUBLIC_KEY ?? "",
});
